import { useEffect } from 'react';
import { COMMANDS, type Command } from '../lib/commands.ts';

const isMac = navigator.platform.toLowerCase().includes('mac');

function eventToShortcut(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (isMac ? e.metaKey : e.ctrlKey) parts.push('Mod');
  if (e.shiftKey && e.key.length > 1) parts.push('Shift');
  else if (e.shiftKey && /^[a-z]$/i.test(e.key)) parts.push('Shift');
  if (e.altKey) parts.push('Alt');
  let key = e.key === ' ' ? 'Space' : e.key;
  if (key.length === 1 && /[a-z]/i.test(key)) key = key.toUpperCase();
  // With Alt on macOS, e.key is a special character; fall back to the physical key.
  if (e.altKey && e.code.startsWith('Key')) key = e.code.slice(3);
  parts.push(key);
  return parts.join('+');
}

function buildMap(): Map<string, Command> {
  const map = new Map<string, Command>();
  for (const c of COMMANDS) {
    const list = Array.isArray(c.shortcut) ? c.shortcut : c.shortcut ? [c.shortcut] : [];
    for (const s of list) map.set(s, c);
  }
  return map;
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  return (
    target instanceof HTMLInputElement && !['checkbox', 'radio', 'range', 'button'].includes(target.type)
  );
}

/** Global keyboard shortcuts (bound once at the app root). */
export function useKeyboardShortcuts(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const map = buildMap();
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const cmd = map.get(eventToShortcut(e));
      if (!cmd) return;
      if (isTyping(e.target) && !cmd.global) return;
      if (document.querySelector('[data-modal-open]') && !cmd.global) return;
      e.preventDefault();
      cmd.run();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
